import { useEffect, useState } from "react";
import {
  X,
  Pencil,
  Save,
  Upload,
  FileSpreadsheet,
  Download,
  CheckCircle2,
  UserX,
  UserCheck,
} from "lucide-react";
import api, { errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Card,
  FilterBar,
  KordaBadge,
  KorwilBadge,
  Pagination,
  Progress,
  SectionTitle,
  SelectFilter,
  StatusBadge,
  TableShell,
  Td,
  TextFilter,
  useDeferredFilters,
  SearchButton,
  Th,
  fmtDate,
} from "@/components/Shared";

function TeknisiDetail({ kode, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/teknisi/${kode}`).then(({ data }) => setD(data)).catch(() => setD(null));
  }, [kode]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/70" onClick={onClose}>
      <div
        data-testid="teknisi-detail-drawer"
        className="h-full w-full max-w-2xl overflow-y-auto border-l border-slate-800 bg-[#0d121c] p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-display text-xl font-bold text-white">
              {d?.teknisi?.nama || "Memuat..."}
            </h3>
            <div className="mt-1 font-mono-data text-xs text-blue-300">{kode}</div>
          </div>
          <button data-testid="close-teknisi-detail" onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        {d && (
          <>
            <div className="mt-5 grid grid-cols-2 gap-3 text-xs sm:grid-cols-3">
              {[
                ["Korwil", d.teknisi.korwil],
                ["Korda", d.teknisi.korda],
                ["Kode Koordinator", d.teknisi.kode_koordinator],
                ["Telepon", d.teknisi.telepon],
                ["Provinsi", d.teknisi.propinsi],
                ["Kabupaten/Kota", d.teknisi.kabupaten_kota],
                ["Kecamatan", d.teknisi.kecamatan || "-"],
                ["Status Validasi", d.teknisi.status_validasi],
                ["Tgl Validasi", d.teknisi.tanggal_validasi],
              ].map(([l, v]) => (
                <div key={l} className="rounded border border-slate-800 bg-slate-900/70 p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">{l}</div>
                  <div className="mt-0.5 truncate text-slate-200">{v || "-"}</div>
                </div>
              ))}
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-5">
              {[
                ["Total Tugas", d.performa.total_tugas, "text-white"],
                ["Selesai", d.performa.selesai, "text-emerald-300"],
                ["Pending", d.performa.pending, "text-amber-300"],
                ["Batal", d.performa.batal, "text-red-300"],
                ["Completion", `${d.performa.completion_rate}%`, "text-blue-300"],
              ].map(([l, v, c]) => (
                <div key={l} className="rounded border border-slate-800 bg-slate-900/70 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">{l}</div>
                  <div className={`font-mono-data text-xl font-semibold ${c}`}>{v}</div>
                </div>
              ))}
            </div>

            <h4 className="mt-6 mb-2 font-display text-base font-semibold text-white">
              Riwayat Penugasan ({d.riwayat.length})
            </h4>
            {d.riwayat.length === 0 ? (
              <div className="rounded border border-slate-800 bg-slate-900/60 py-8 text-center text-xs text-slate-500">
                Teknisi ini belum memiliki penugasan.
              </div>
            ) : (
              <div className="overflow-x-auto rounded border border-slate-800">
                <table className="w-full min-w-[560px]">
                  <thead>
                    <tr>
                      <Th>id_transaksi</Th>
                      <Th>Sekolah</Th>
                      <Th>Jadwal</Th>
                      <Th>Status</Th>
                      <Th>BAPP</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.riwayat.map((r) => (
                      <tr key={r.id_transaksi} className="border-b border-slate-800/70">
                        <Td className="font-mono-data text-xs text-blue-300">{r.id_transaksi}</Td>
                        <Td className="max-w-[180px] truncate text-xs">{r.sekolah}</Td>
                        <Td className="font-mono-data text-xs">{fmtDate(r.jadwal)}</Td>
                        <Td><StatusBadge value={r.ops?.status} /></Td>
                        <Td><StatusBadge value={r.ops?.bapp_status} /></Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function Teknisi() {
  const { korwilFilter, user } = useAuth();
  const [opts, setOpts] = useState({});
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const { draft: f, applied: appliedF, set, apply, reset } = useDeferredFilters({ kabupaten: "", kecamatan: "", propinsi: "", korwil: "", status: "", q: "", aktif: "" });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [res, setRes] = useState({ items: [], total: 0, total_master: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [techEditing, setTechEditing] = useState(null);
  const [techSaving, setTechSaving] = useState(false);
  const [deactivating, setDeactivating] = useState(null);

  // Bulk upload master teknisi - SUPER_ADMIN only
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importSaving, setImportSaving] = useState(false);

  useEffect(() => {
    api.get("/filters/options").then(({ data }) => setOpts(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { ...f, korwil: korwilFilter || undefined, page, per_page: perPage };
    Object.keys(params).forEach((k) => !params[k] && delete params[k]);
    api
      .get("/teknisi", { params })
      .then(({ data }) => setRes(data))
      .catch(() => setRes({ items: [], total: 0, total_master: 0 }))
      .finally(() => setLoading(false));
  }, [appliedF, korwilFilter, page, perPage]);

  const canEditKorda =
    user?.role === "SUPER_ADMIN" || user?.role === "KORWIL" || user?.role === "ADMIN_KORWIL";

  const canManageTeknisi =
    user?.role === "SUPER_ADMIN" || user?.role === "KORWIL" || user?.role === "ADMIN_KORWIL";

  const refreshTeknisi = async () => {
    const params = {
      ...f,
      korwil: korwilFilter || undefined,
      page,
      per_page: perPage,
    };

    Object.keys(params).forEach((k) => {
      if (!params[k]) delete params[k];
    });

    const { data } = await api.get("/teknisi", { params });
    setRes(data);
  };

  const saveTeknisi = async () => {
    if (!techEditing) return;

    setTechSaving(true);

    try {
      await api.patch(`/teknisi/${techEditing.kode}`, {
        nama: techEditing.nama,
        telepon: techEditing.telepon,
        propinsi: techEditing.propinsi,
        kabupaten_kota: techEditing.kabupaten_kota,
        kecamatan: techEditing.kecamatan,
        status_validasi: techEditing.status_validasi,
      });

      setTechEditing(null);
      await refreshTeknisi();
    } catch (e) {
      alert(errText(e));
    } finally {
      setTechSaving(false);
    }
  };

  const nonaktifkanTeknisi = async (t) => {
    const ok = window.confirm(
      `Nonaktifkan teknisi ini?\n\n${t.nama}\n${t.kode_teknisi}\n\n` +
      `Data tidak akan dihapus permanen dan masih bisa dipulihkan oleh Super Admin.`
    );

    if (!ok) return;

    setDeactivating(t.kode_teknisi);

    try {
      await api.post(`/teknisi/${t.kode_teknisi}/nonaktifkan`);
      await refreshTeknisi();
    } catch (e) {
      alert(errText(e));
    } finally {
      setDeactivating(null);
    }
  };

  const aktifkanTeknisi = async (t) => {
    const ok = window.confirm(
      `Aktifkan kembali teknisi ini?\n\n${t.nama}\n${t.kode_teknisi}`
    );

    if (!ok) return;

    setDeactivating(t.kode_teknisi);

    try {
      await api.post(`/teknisi/${t.kode_teknisi}/aktifkan`);
      await refreshTeknisi();
    } catch (e) {
      alert(errText(e));
    } finally {
      setDeactivating(null);
    }
  };

  const saveKorda = async () => {
    if (!editing) return;

    setSaving(true);

    try {
      const korda = editing.manual.trim()
        ? editing.manual.trim()
        : editing.selected || null;

      await api.patch(
        `/teknisi/${editing.kode}/korda`,
        { korda }
      );

      setEditing(null);

      const params = {
        ...f,
        korwil: korwilFilter || undefined,
        page,
        per_page: perPage,
      };

      Object.keys(params).forEach(
        (k) => !params[k] && delete params[k]
      );

      const { data } = await api.get("/teknisi", { params });
      setRes(data);
    } catch (e) {
      alert(errText(e));
    } finally {
      setSaving(false);
    }
  };

  const previewImport = async () => {
    if (!importFile) {
      alert("Pilih file Excel atau CSV terlebih dahulu.");
      return;
    }

    setImportLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", importFile);

      const response = await api.post(
        "/teknisi/import/preview",
        formData
      );

      console.log("PREVIEW RESPONSE:", response.data);

      setImportPreview(response.data);

      alert(
        "Preview berhasil diterima.\n\n" +
        `Total: ${response.data?.total_baris || 0}\n` +
        `Baru: ${response.data?.baru || 0}\n` +
        `Update: ${response.data?.update || 0}\n` +
        `Duplikat: ${response.data?.duplikat || 0}\n` +
        `Tidak valid: ${response.data?.tidak_valid || 0}\n` +
        `Preview rows: ${response.data?.preview?.length || 0}`
      );
    } catch (e) {
      console.error("PREVIEW ERROR:", e);

      alert(
        "PREVIEW GAGAL\n\n" +
        `HTTP: ${e?.response?.status || "-"}\n` +
        `Pesan: ${errText(e)}`
      );

      setImportPreview(null);
    } finally {
      setImportLoading(false);
    }
  };

  const confirmImport = async () => {
    if (!importFile || !importPreview) return;

    const ok = window.confirm(
      `Import ${Number(importPreview.total_baris || 0).toLocaleString("id-ID")} baris?\n\n` +
      `Data baru: ${Number(importPreview.baru || 0).toLocaleString("id-ID")}\n` +
      `Data update: ${Number(importPreview.update || 0).toLocaleString("id-ID")}\n` +
      `Duplikat: ${Number(importPreview.duplikat || 0).toLocaleString("id-ID")}\n` +
      `Tidak valid: ${Number(importPreview.tidak_valid || 0).toLocaleString("id-ID")}\n\n` +
      `Data teknisi lama yang tidak ada di file TIDAK akan dihapus.`
    );

    if (!ok) return;

    setImportSaving(true);

    try {
      const formData = new FormData();
      formData.append("file", importFile);

      const { data } = await api.post(
        "/teknisi/import",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      alert(
        `Import berhasil.\n\n` +
        `Teknisi baru: ${data.inserted || 0}\n` +
        `Teknisi diperbarui: ${data.updated || 0}\n` +
        `Dilewati: ${data.skipped || 0}\n` +
        `Duplikat: ${data.duplicate || 0}`
      );

      setImportOpen(false);
      setImportFile(null);
      setImportPreview(null);
      setPage(1);

      const params = {
        ...f,
        korwil: korwilFilter || undefined,
        page: 1,
        per_page: perPage,
      };

      Object.keys(params).forEach((k) => {
        if (!params[k]) delete params[k];
      });

      const { data: refreshed } = await api.get("/teknisi", { params });
      setRes(refreshed);

    } catch (e) {
      alert(errText(e));
    } finally {
      setImportSaving(false);
    }
  };

  const downloadTemplate = () => {
    const a = document.createElement("a");
    a.href = "/template_master_teknisi.xlsx";
    a.download = "template_master_teknisi.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const applyFilters = () => { setPage(1); apply(); };

  return (
    <div data-testid="page-teknisi">
      <SectionTitle
        title="Database Teknisi"
        subtitle="Master database teknisi — termasuk teknisi tanpa penugasan"
        right={
          <div className="flex flex-wrap items-center justify-end gap-3">
            {user?.role === "SUPER_ADMIN" && (
              <button
                type="button"
                data-testid="upload-master-teknisi"
                onClick={() => {
                  setImportOpen(true);
                  setImportFile(null);
                  setImportPreview(null);
                }}
                className="flex items-center gap-2 rounded-lg border border-blue-700 bg-blue-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-blue-500"
              >
                <Upload className="h-3.5 w-3.5" />
                Upload Master Teknisi
              </button>
            )}

            <Card className="px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Total Master Teknisi</div>
              <div data-testid="total-master-teknisi" className="font-mono-data text-lg font-semibold text-white">
                {(res.total_master || 0).toLocaleString("id-ID")}
              </div>
            </Card>
            <Card className="px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Hasil Filter</div>
              <div data-testid="total-filtered-teknisi" className="font-mono-data text-lg font-semibold text-blue-300">
                {(res.total || 0).toLocaleString("id-ID")}
              </div>
            </Card>
          </div>
        }
      />

      <FilterBar>
        <TextFilter label="Cari" testId="teknisi-search" value={f.q} onChange={set("q")} placeholder="Kode / Nama / Telepon" />
        <SelectFilter label="Provinsi" testId="teknisi-filter-propinsi" value={f.propinsi} onChange={set("propinsi")} options={opts.propinsi} />
        <SelectFilter label="Kabupaten/Kota" testId="teknisi-filter-kabupaten" value={f.kabupaten} onChange={set("kabupaten")} options={opts.kabupaten_kota} />
        <SelectFilter label="Kecamatan" testId="teknisi-filter-kecamatan" value={f.kecamatan} onChange={set("kecamatan")} options={opts.kecamatan} />
        <SelectFilter label="Koordinator Area" testId="teknisi-filter-area" value={f.korwil} onChange={set("korwil")} options={opts.korwil} allLabel="Semua Korwil" />
        <SelectFilter label="Status" testId="teknisi-filter-status" value={f.status} onChange={set("status")} options={opts.status_validasi} />

              <SelectFilter
                label="Status Data"
                testId="teknisi-filter-aktif"
                value={f.aktif}
                onChange={set("aktif")}
                options={["true", "false"]}
                optionLabels={{
                  true: "Aktif",
                  false: "Nonaktif",
                }}
                allLabel="Semua"
              />
        <button
          data-testid="teknisi-reset"
          onClick={reset}
          className="rounded border border-slate-700 px-3 py-1.5 text-xs text-slate-300 transition-colors hover:bg-slate-800 hover:text-white"
        >
          Reset
        </button>
      
        <SearchButton onClick={applyFilters} testId="teknisi-search-button" /></FilterBar>

      <TableShell testId="teknisi-table" loading={loading} empty={res.items.length === 0}>
        <thead>
          <tr>
            <Th>ID Teknisi</Th>
            <Th>Nama</Th>
            <Th>Telepon</Th>
            <Th>Provinsi</Th>
            <Th>Kabupaten/Kota</Th>
            <Th>Kecamatan</Th>
            <Th>Korda</Th>
            <Th>Korwil</Th>
            <Th>Koord. Kode</Th>
            <Th>Status</Th>
            <Th className="text-right">Tugas</Th>
            {canManageTeknisi && <Th className="text-right">Aksi</Th>}
          </tr>
        </thead>
        <tbody>
          {res.items.map((t) => (
            <tr
              key={t.kode_teknisi}
              data-testid={`teknisi-row-${t.kode_teknisi}`}
              onClick={() => setSelected(t.kode_teknisi)}
              className="cursor-pointer border-b border-slate-800/70 transition-colors hover:bg-slate-800/50"
            >
              <Td className="font-mono-data text-xs text-blue-300">{t.kode_teknisi}</Td>
              <Td className="text-slate-100">{t.nama}</Td>
              <Td className="font-mono-data text-xs">{t.telepon}</Td>
              <Td className="text-xs">{t.propinsi}</Td>
              <Td className="text-xs">{t.kabupaten_kota}</Td>
              <Td className="text-xs">{t.kecamatan || "-"}</Td>
              <Td><KordaBadge value={t.korda} /></Td>
              <Td><KorwilBadge value={t.korwil} /></Td>
              <Td className="font-mono-data text-[11px]">{t.kode_koordinator || "-"}</Td>
              <Td><StatusBadge value={t.status_validasi} /></Td>
              <Td className="text-right font-mono-data text-xs">
                <span className={t.total_tugas === 0 ? "text-slate-500" : "text-slate-100"}>
                  {t.tugas_selesai}/{t.total_tugas}
                </span>
              </Td>

              {canManageTeknisi && (
                <Td className="text-right">
                  <div className="flex justify-end gap-1.5">

                    {canManageTeknisi && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();

                        setTechEditing({
                          kode: t.kode_teknisi,
                          nama: t.nama || "",
                          telepon: t.telepon || "",
                          propinsi: t.propinsi || "",
                          kabupaten_kota: t.kabupaten_kota || "",
                          kecamatan: t.kecamatan || "",
                          status_validasi: t.status_validasi || "",
                        });
                      }}
                      className="inline-flex items-center gap-1 rounded-lg border border-blue-800 bg-blue-950/40 px-2.5 py-1.5 text-[10px] font-semibold text-blue-300 hover:bg-blue-900/50"
                    >
                      <Pencil className="h-3 w-3" />
                      Edit
                    </button>
                    )}

                    
                    {canEditKorda && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();

                          const daftarKorda = [
                            "Anggana",
                            "Reffi",
                            "Leo",
                            "Syahlani",
                            "Elsa",
                            "Adel",
                            "Viki",
                          ];

                          const current = t.korda || "";

                          setEditing({
                            kode: t.kode_teknisi,
                            nama: t.nama,
                            selected: daftarKorda.includes(current) ? current : "",
                            manual: current && !daftarKorda.includes(current) ? current : "",
                          });
                        }}
                        className="inline-flex items-center gap-1 rounded-lg border border-violet-800 bg-violet-950/40 px-2.5 py-1.5 text-[10px] font-semibold text-violet-300 hover:bg-violet-900/50"
                      >
                        <Pencil className="h-3 w-3" />
                        Korda
                      </button>
                    )}

                    {t.aktif === false ? (
                      user?.role === "SUPER_ADMIN" && (
                        <button
                          type="button"
                          disabled={deactivating === t.kode_teknisi}
                          onClick={(e) => {
                            e.stopPropagation();
                            aktifkanTeknisi(t);
                          }}
                          className="inline-flex items-center gap-1 rounded-lg border border-emerald-800 bg-emerald-950/40 px-2.5 py-1.5 text-[10px] font-semibold text-emerald-300 hover:bg-emerald-900/50 disabled:opacity-50"
                        >
                          <UserCheck className="h-3 w-3" />
                          Aktifkan
                        </button>
                      )
                    ) : (
                      <button
                        type="button"
                        disabled={deactivating === t.kode_teknisi}
                        onClick={(e) => {
                          e.stopPropagation();
                          nonaktifkanTeknisi(t);
                        }}
                        className="inline-flex items-center gap-1 rounded-lg border border-red-800 bg-red-950/40 px-2.5 py-1.5 text-[10px] font-semibold text-red-300 hover:bg-red-900/50 disabled:opacity-50"
                      >
                        <UserX className="h-3 w-3" />
                        {deactivating === t.kode_teknisi
                          ? "Proses..."
                          : "Nonaktifkan"}
                      </button>
                    )}

                  </div>
                </Td>
              )}}
            </tr>
          ))}
        </tbody>
      </TableShell>
      <Pagination page={res.page || page} perPage={perPage} total={res.total} onPage={setPage} onPerPage={(n) => { setPerPage(n); setPage(1); }} testId="teknisi-pagination" />

      {selected && <TeknisiDetail kode={selected} onClose={() => setSelected(null)} />}

      {techEditing && canManageTeknisi && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4"
          onClick={() => {
            if (!techSaving) setTechEditing(null);
          }}
        >
          <div
            className="w-full max-w-xl rounded-xl border border-slate-700 bg-[#0d121c] p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-display text-lg font-semibold text-white">
                  Edit Data Teknisi
                </h3>
                <p className="mt-1 font-mono-data text-xs text-blue-300">
                  {techEditing.kode}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setTechEditing(null)}
                disabled={techSaving}
                className="text-slate-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {[
                ["nama", "Nama"],
                ["telepon", "No. HP"],
                ["propinsi", "Provinsi"],
                ["kabupaten_kota", "Kabupaten/Kota"],
                ["kecamatan", "Kecamatan"],
                ["status_validasi", "Status Validasi"],
              ].map(([key, label]) => (
                <label key={key} className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-wider text-slate-500">
                    {label}
                  </span>

                  <input
                    value={techEditing[key] || ""}
                    onChange={(e) =>
                      setTechEditing((prev) => ({
                        ...prev,
                        [key]: e.target.value,
                      }))
                    }
                    className="rounded border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-200 outline-none focus:border-blue-500"
                  />
                </label>
              ))}
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setTechEditing(null)}
                disabled={techSaving}
                className="rounded border border-slate-700 px-3 py-2 text-xs text-slate-300 hover:bg-slate-800"
              >
                Batal
              </button>

              <button
                type="button"
                onClick={saveTeknisi}
                disabled={techSaving}
                className="inline-flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-xs font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
              >
                <Save className="h-3.5 w-3.5" />
                {techSaving ? "Menyimpan..." : "Simpan"}
              </button>
            </div>
          </div>
        </div>
      )}


      {importOpen && user?.role === "SUPER_ADMIN" && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4"
          onClick={() => {
            if (!importLoading && !importSaving) {
              setImportOpen(false);
            }
          }}
        >
          <div
            className="w-full max-w-3xl rounded-xl border border-slate-700 bg-[#0d121c] p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-white">
                  <FileSpreadsheet className="h-5 w-5 text-blue-400" />
                  Upload Master Teknisi
                </h3>

                <p className="mt-1 text-xs text-slate-500">
                  Upload Excel (.xlsx/.xls) atau CSV.
                </p>
              </div>

              <button
                type="button"
                disabled={importLoading || importSaving}
                onClick={() => setImportOpen(false)}
                className="text-slate-400 hover:text-white disabled:opacity-40"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-5 rounded-lg border border-slate-800 bg-slate-950/50 p-4">
              <div className="mb-3 text-xs text-slate-400">
                Gunakan template agar nama kolom sesuai dengan database.
              </div>

              <button
                type="button"
                onClick={downloadTemplate}
                className="flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-xs font-semibold text-slate-300 transition hover:bg-slate-800 hover:text-white"
              >
                <Download className="h-3.5 w-3.5" />
                Download Template Excel
              </button>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-blue-700 bg-blue-950/40 px-3 py-2 text-xs font-semibold text-blue-300 transition hover:bg-blue-900/50">
                <Upload className="h-3.5 w-3.5" />
                Pilih File

                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={(e) => {
                    setImportFile(e.target.files?.[0] || null);
                    setImportPreview(null);
                  }}
                />
              </label>

              {importFile && (
                <span className="text-xs text-slate-400">
                  {importFile.name}
                </span>
              )}
            </div>

            {importFile && !importPreview && (
              <button
                type="button"
                disabled={importLoading}
                onClick={previewImport}
                className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-blue-500 disabled:opacity-50"
              >
                {importLoading ? "Memeriksa..." : "Preview Data"}
              </button>
            )}

            {importPreview && (
              <>
                <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div className="rounded-lg border border-slate-700 bg-slate-900/70 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">
                      Total
                    </div>
                    <div className="mt-1 font-mono-data text-xl font-semibold text-white">
                      {Number(importPreview.total_baris || 0).toLocaleString("id-ID")}
                    </div>
                  </div>

                  <div className="rounded-lg border border-emerald-800/70 bg-emerald-950/20 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">
                      Data Baru
                    </div>
                    <div className="mt-1 font-mono-data text-xl font-semibold text-emerald-300">
                      {Number(importPreview.baru || 0).toLocaleString("id-ID")}
                    </div>
                  </div>

                  <div className="rounded-lg border border-blue-800/70 bg-blue-950/20 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">
                      Update
                    </div>
                    <div className="mt-1 font-mono-data text-xl font-semibold text-blue-300">
                      {Number(importPreview.update || 0).toLocaleString("id-ID")}
                    </div>
                  </div>

                  <div className="rounded-lg border border-red-800/70 bg-red-950/20 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">
                      Tidak Valid
                    </div>
                    <div className="mt-1 font-mono-data text-xl font-semibold text-red-300">
                      {Number(importPreview.tidak_valid || 0).toLocaleString("id-ID")}
                    </div>
                  </div>
                </div>

                <div className="mt-4 rounded-lg border border-slate-800 bg-slate-950/50 p-3 text-xs text-slate-400">
                  <div className="flex items-center gap-2 text-emerald-300">
                    <CheckCircle2 className="h-4 w-4" />
                    Preview berhasil
                  </div>

                  <p className="mt-2">
                    Data teknisi lama yang tidak ada di file tidak akan dihapus.
                  </p>
                </div>

                {importPreview.preview?.length > 0 && (
                  <div className="mt-4 max-h-56 overflow-auto rounded-lg border border-slate-800">
                    <table className="w-full text-left text-xs">
                      <thead className="sticky top-0 bg-slate-900">
                        <tr>
                          <Th>Kode</Th>
                          <Th>Nama</Th>
                          <Th>Korda</Th>
                          <Th>Korwil</Th>
                          <Th className="whitespace-nowrap">Kode Koordinator</Th>
                          <Th>Status</Th>
                        </tr>
                      </thead>

                      <tbody>
                        {importPreview.preview.map((r, i) => (
                          <tr
                            key={`${r.kode_teknisi}-${i}`}
                            className="border-b border-slate-800/70"
                          >
                            <Td className="font-mono-data text-blue-300">
                              {r.kode_teknisi}
                            </Td>
                            <Td>{r.nama}</Td>
                            <Td>{r.korda || "-"}</Td>
                            <Td>{r.korwil || "-"}</Td>
                            <Td className="font-mono-data text-amber-300">
                              {r.kode_koordinator || "-"}
                            </Td>
                            <Td>{r._status || "-"}</Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="mt-5 flex justify-end gap-2">
                  <button
                    type="button"
                    disabled={importSaving}
                    onClick={() => {
                      setImportFile(null);
                      setImportPreview(null);
                    }}
                    className="rounded-lg border border-slate-700 px-4 py-2 text-xs text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                  >
                    Ganti File
                  </button>

                  <button
                    type="button"
                    disabled={importSaving}
                    onClick={confirmImport}
                    className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {importSaving ? "Mengimport..." : "Konfirmasi Import"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {editing && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
          onClick={() => !saving && setEditing(null)}
        >
          <div
            className="w-full max-w-md rounded-xl border border-slate-700 bg-[#0d121c] p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-5 flex items-start justify-between">
              <div>
                <h3 className="font-display text-lg font-semibold text-white">
                  Edit Korda Teknisi
                </h3>
                <p className="mt-1 text-xs text-slate-500">
                  {editing.nama} · {editing.kode}
                </p>
              </div>

              <button
                type="button"
                onClick={() => !saving && setEditing(null)}
                className="text-slate-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <label className="mb-2 block text-[10px] uppercase tracking-wider text-slate-500">
              Nama Korda
            </label>

            <select
              value={editing.selected}
              onChange={(e) =>
                setEditing((p) => ({
                  ...p,
                  selected: e.target.value,
                  manual: "",
                }))
              }
              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white outline-none focus:border-blue-500"
            >
              <option value="">Tidak Ditentukan / Internal Korwil</option>
              <option value="Anggana">Anggana</option>
              <option value="Reffi">Reffi</option>
              <option value="Leo">Leo</option>
              <option value="Syahlani">Syahlani</option>
              <option value="Elsa">Elsa</option>
              <option value="Adel">Adel</option>
              <option value="Viki">Viki</option>
            </select>

            <div className="my-3 text-center text-[10px] uppercase tracking-wider text-slate-600">
              atau isi manual
            </div>

            <input
              value={editing.manual}
              onChange={(e) =>
                setEditing((p) => ({
                  ...p,
                  manual: e.target.value,
                  selected: "",
                }))
              }
              placeholder="Nama Korda lainnya..."
              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white outline-none placeholder:text-slate-600 focus:border-blue-500"
            />

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                disabled={saving}
                onClick={() => setEditing(null)}
                className="rounded-lg border border-slate-700 px-4 py-2 text-xs text-slate-300 hover:bg-slate-800"
              >
                Batal
              </button>

              <button
                type="button"
                disabled={saving}
                onClick={saveKorda}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-xs font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
              >
                <Save className="h-3.5 w-3.5" />
                {saving ? "Menyimpan..." : "Simpan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
